import { useEffect, useRef, useState } from "react";
import { BookOpen, ExternalLink, ChevronDown, ChevronUp, Loader2, Cpu, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useMatchingManuals, type RepairManual } from "@/hooks/useMatchingManuals";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Props {
  brand?: string | null;
  modelNumber?: string | null;
  defaultOpen?: boolean;
  /** Currently active manual id (for highlighting + AI assistant) */
  activeManualId?: string | null;
  /** Called when user picks a manual to use with the AI assistant.
   *  `kind` indicates whether this is an exact model match or a brand fallback. */
  onSelectForAI?: (manualId: string, title: string, kind: "exact" | "fallback") => void;
  /** Auto-attach the best-ranked manual when no manual is currently active.
   *  Fires once per panel mount. Default: true when onSelectForAI is provided. */
  autoAttachTopMatch?: boolean;
}

const titleOf = (m: RepairManual, fallback?: string) => m.title || m.original_filename || fallback || "Untitled manual";

export function MatchingManualsPanel({
  brand,
  modelNumber,
  defaultOpen = false,
  activeManualId,
  onSelectForAI,
  autoAttachTopMatch,
}: Props) {
  const { t } = useTranslation("appliance");
  const { exactMatches, fallbackMatches, topMatch, loading } = useMatchingManuals(brand, modelNumber);
  const [open, setOpen] = useState(defaultOpen);
  const autoAttachedRef = useRef(false);

  const shouldAutoAttach = autoAttachTopMatch ?? Boolean(onSelectForAI);

  // Auto-attach the next-best manual when no exact match is selected.
  // Only fires once and only when the user hasn't already picked one.
  useEffect(() => {
    if (!shouldAutoAttach || !onSelectForAI) return;
    if (loading || autoAttachedRef.current) return;
    if (activeManualId) return; // user already picked one
    if (!topMatch) return;
    autoAttachedRef.current = true;
    const titleForToast = titleOf(topMatch, t("manuals.untitled"));
    onSelectForAI(topMatch.id, titleForToast, topMatch.kind ?? "exact");
    if (topMatch.kind === "fallback") {
      toast.info(
        t("manuals.noExactToast", {
          query: modelNumber || brand,
          brand,
          title: titleForToast,
        })
      );
    }
  }, [shouldAutoAttach, onSelectForAI, loading, topMatch, activeManualId, brand, modelNumber, t]);

  const openManual = async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from("repair-guides")
      .createSignedUrl(filePath, 300);
    if (error || !data?.signedUrl) {
      toast.error(t("manuals.couldNotOpen"));
      return;
    }
    const a = document.createElement("a");
    a.href = data.signedUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!brand) return null;
  const total = exactMatches.length + fallbackMatches.length;
  if (!loading && total === 0) return null;

  const summary = loading
    ? t("manuals.searching")
    : exactMatches.length > 0
      ? modelNumber
        ? t("manuals.matchingForModel", { count: exactMatches.length, brand, model: modelNumber })
        : t("manuals.matchingFor", { count: exactMatches.length, brand })
      : t("manuals.noExact", { count: fallbackMatches.length, brand });

  const renderRow = (m: RepairManual) => {
    const isActive = activeManualId === m.id;
    return (
      <div key={m.id} className={`p-4 flex items-center justify-between gap-3 ${isActive ? "bg-primary/5" : ""}`}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground truncate">{titleOf(m, t("manuals.untitled"))}</p>
            {isActive && (
              <Badge variant="default" className="text-xs gap-1">
                <Check className="w-3 h-3" /> {t("manuals.active")}
              </Badge>
            )}
            {m.kind === "fallback" && !isActive && (
              <Badge variant="secondary" className="text-xs gap-1">
                <Sparkles className="w-3 h-3" /> {t("manuals.closest")}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {m.document_type && <Badge variant="secondary" className="text-xs">{m.document_type.replace(/_/g, " ")}</Badge>}
            {m.language && <Badge variant="outline" className="text-xs uppercase">{m.language}</Badge>}
            {m.market && <Badge variant="outline" className="text-xs">{m.market}</Badge>}
            {m.models?.slice(0, 3).map(mod => (
              <Badge key={mod} variant="outline" className="text-xs">{mod}</Badge>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onSelectForAI && (
            <Button
              size="sm"
              variant={isActive ? "default" : "outline"}
              onClick={() => onSelectForAI(m.id, titleOf(m, t("manuals.untitled")), m.kind ?? "exact")}
              title={t("manuals.useWithAiTitle")}
            >
              <Cpu className="w-3.5 h-3.5 mr-1" />
              {isActive ? t("manuals.active") : t("manuals.useWithAi")}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => openManual(m.file_path)}>
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> {t("manuals.open")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-primary" />
          </div>
          <div className="text-left">
            <p className="font-medium text-foreground">{t("manuals.title")}</p>
            <p className="text-xs text-muted-foreground">{summary}</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border">
          {loading && (
            <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("manuals.loading")}
            </div>
          )}
          {exactMatches.length > 0 && (
            <div className="divide-y divide-border">{exactMatches.map(renderRow)}</div>
          )}
          {exactMatches.length === 0 && fallbackMatches.length > 0 && (
            <>
              <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                {modelNumber
                  ? t("manuals.fallbackNote", { model: modelNumber, brand })
                  : t("manuals.fallbackNoteNoModel", { brand })}
              </div>
              <div className="divide-y divide-border">{fallbackMatches.slice(0, 5).map(renderRow)}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
